import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { FullPageSpinner } from './components/common/LoadingSpinner'

// Pages
import Home from './pages/Home'
import Landing from './pages/Landing'
import Login from './pages/Login'
import Register from './pages/Register'
import HuntList from './pages/HuntList'
import HuntDetail from './pages/HuntDetail'
import ActiveHunt from './pages/ActiveHunt'
import Profile from './pages/Profile'
import Badges from './pages/Badges'
import HuntHistory from './pages/HuntHistory'
import MyRewards from './pages/MyRewards'

// Admin pages
import AdminLayout from './admin/AdminLayout'
import AdminHuntList from './admin/pages/HuntList'
import HuntEditor from './admin/pages/HuntEditor'
import Analytics from './admin/pages/Analytics'
import AdminSettings from './admin/pages/Settings'
import AdminUsers from './admin/pages/Users'
import AdminReports from './admin/pages/Reports'
import SponsorDashboard from './admin/pages/SponsorDashboard'

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, initialized } = useAuth()

  if (!initialized || loading) {
    return <FullPageSpinner />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export default function App() {
  const { initialized, isAuthenticated } = useAuth()

  if (!initialized) {
    return <FullPageSpinner />
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Routes>
        {/* Public routes */}
        <Route path="/" element={isAuthenticated ? <Home /> : <Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/hunts" element={<HuntList />} />
        <Route path="/hunts/:huntId" element={<HuntDetail />} />

        {/* Protected routes */}
        <Route
          path="/hunts/:huntId/play"
          element={
            <ProtectedRoute>
              <ActiveHunt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/badges"
          element={
            <ProtectedRoute>
              <Badges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <HuntHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rewards"
          element={
            <ProtectedRoute>
              <MyRewards />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/hunts" replace />} />
          <Route path="hunts" element={<AdminHuntList />} />
          <Route path="hunts/:huntId" element={<HuntEditor />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="sponsor" element={<SponsorDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  )
}
